import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { z } from "zod"
import { useState, useRef, useCallback } from "react"
import { useWalletsActions } from "@/api/wallets"
import Portal, { BackupMethods } from "@portal-hq/web"
import CreateWalletForm from "./create-wallet.form"
import Loader from "@/components/layout/loader"
import { formSchema } from "./create-wallet-form.schema"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { DialogClose } from "@radix-ui/react-dialog"
import { WalletBackupModal } from "./wallet-backup.modal"
import { useWalletBackup, useWalletStoreActions } from "@/state/stores/wallet.store"

interface CreateWalletModalProps {
  children: React.ReactNode;
  onComplete?: () => void;
}

const PORTAL_CREATE_STEPS = 11

export function CreateWalletModal({ children, onComplete }: CreateWalletModalProps) {
  const { create, update, confirm, updateBackups } = useWalletsActions()
  const { setPortal, setBackupMethod, setBackupConfigs } = useWalletStoreActions()
  const { backupSuggestionDismissed, dismissBackupSuggestion } = useWalletBackup()
  // Track whether a creation flow is currently in progress to avoid duplicate POST /wallets
  const creatingRef = useRef(false)
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState<"form" | "processing" | "done">("form")
  const [progress, setProgress] = useState<number>(0)

  const finishFlow = useCallback(() => {
    onComplete?.()
    setOpen(false)
  }, [onComplete])

  const handleBackupDismiss = useCallback(() => {
    dismissBackupSuggestion()
    finishFlow()
  }, [dismissBackupSuggestion, finishFlow])

  const handleBackupComplete = useCallback(() => {
    dismissBackupSuggestion()
    finishFlow()
  }, [dismissBackupSuggestion, finishFlow])

  const onSubmit = useCallback(async ({ password }: z.infer<typeof formSchema>) => {
    if (creatingRef.current) {
      return // Ignore duplicate submissions while already creating
    }
    creatingRef.current = true
    setStep("processing")
    const wallet = await create()
    const portal = new Portal({
      apiKey: wallet.portalApiKey,
      rpcConfig: {},
      host: "portal.braidpay.com",
    })
    const backupConfigs = { passwordStorage: { password } }
    const backupMethod = BackupMethods.password

    portal.onReady(async () => {
      await portal.createWallet(() =>
        setProgress((progress: number) => progress + 1),
      )
      const [ethAddress, solAddress] = await Promise.all([
        portal.getEip155Address(),
        portal.getSolanaAddress(),
      ])
      const { cipherText } = await portal.backupWallet(
        backupMethod,
        () => setProgress((progress: number) => progress + 1),
        backupConfigs,
      )
      await Promise.all([
        update(ethAddress, solAddress),
        updateBackups("PASSWORD", cipherText),
      ])
      setProgress((progress: number) => progress + 1)
      await portal.storedClientBackupShare(true, backupMethod)
      await confirm()
      setProgress((progress: number) => progress + 1)
      setPortal(portal)
      setBackupMethod(backupMethod)
      setBackupConfigs(backupConfigs)
      setStep("done")
      creatingRef.current = false
    })
  }, [create, update, confirm, updateBackups, setPortal, setBackupMethod, setBackupConfigs])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent
        className="sm:max-w-[425px]"
        onInteractOutside={(e) => step === "processing" && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Wallet Setup</DialogTitle>
        </DialogHeader>
        {step === "processing" && (
          <div className="flex gap-4 flex-col">
            <Loader message="Creating wallet, this can take a minute..." />
            <Progress value={(progress * 100) / PORTAL_CREATE_STEPS} />
          </div>
        )}
        {step === "form" && (
          <>
            <DialogDescription>
              Before receiving payments, please set up your wallet. Create a
              secret pin you will use for withdrawals.
            </DialogDescription>
            <CreateWalletForm onSubmit={onSubmit} />
          </>
        )}
        {step === "done" && (
          <>
            <Alert variant="success">
              <AlertTitle>Done!</AlertTitle>
              <AlertDescription>
                Your wallet has been created successfully.
              </AlertDescription>
            </Alert>
            {!backupSuggestionDismissed && (
              <>
                <p className="text-sm text-secondary-foreground">
                  It's a good idea to backup your wallet now, in case you forget
                  your password in the future:
                </p>
                <WalletBackupModal
                  method={BackupMethods.gdrive}
                  onSkip={handleBackupDismiss}
                  onDone={handleBackupComplete}
                >
                  <Button variant="outline">Backup with Google Drive</Button>
                </WalletBackupModal>
                <WalletBackupModal
                  method={BackupMethods.passkey}
                  onSkip={handleBackupDismiss}
                  onDone={handleBackupComplete}
                >
                  <Button variant="outline">Backup with Passkey</Button>
                </WalletBackupModal>
                <div className="flex gap-2 flex-wrap">
                  <DialogClose asChild>
                    <Button data-testid="close" variant="secondary" onClick={finishFlow}>Close</Button>
                  </DialogClose>
                  <Button
                    variant="ghost"
                    onClick={handleBackupDismiss}
                    data-testid="skip-backup"
                  >
                    Skip for now
                  </Button>
                </div>
              </>
            )}
            {backupSuggestionDismissed && (
              <DialogClose asChild>
                <Button data-testid="close" onClick={finishFlow}>Close</Button>
              </DialogClose>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
